import { useState, useEffect, useCallback } from 'react'
import { getPage, post, patch as patchReq, del as delReq } from '../lib/api'

export interface CoverLetterSections {
  problem: string
  strategy: string
  execution: string
  result: string
}

export interface CoverLetterCharCounts {
  problem: number
  strategy: number
  execution: number
  result: number
}

export interface CoverLetter {
  id: string
  talentId: string
  jobMatchId: string | null
  company: string
  sections: CoverLetterSections
  charCounts: CoverLetterCharCounts | null
  packagingLevel: number
  createdAt: string
}

export function useCoverLetters(talentId: string) {
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getPage<CoverLetter>(
        `/api/v1/talents/${talentId}/cover-letters`,
      )
      setCoverLetters(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류')
    } finally {
      setLoading(false)
    }
  }, [talentId])

  useEffect(() => {
    load()
  }, [load])

  const generate = useCallback(
    async (company: string, packagingLevel?: number) => {
      setGenerating(true)
      try {
        await post<CoverLetter>(
          `/api/v1/talents/${talentId}/cover-letters/generate`,
          { company, ...(packagingLevel != null ? { packaging_level: packagingLevel } : {}) },
        )
        await load()
      } finally {
        setGenerating(false)
      }
    },
    [talentId, load],
  )

  const update = useCallback(
    async (
      id: string,
      data: { company?: string; sections?: CoverLetterSections; packagingLevel?: number },
    ) => {
      const body: Record<string, unknown> = {}
      if (data.company !== undefined) body.company = data.company
      if (data.sections !== undefined) body.sections = data.sections
      if (data.packagingLevel !== undefined) body.packaging_level = data.packagingLevel
      await patchReq(`/api/v1/talents/${talentId}/cover-letters/${id}`, body)
      await load()
    },
    [talentId, load],
  )

  const remove = useCallback(
    async (id: string) => {
      await delReq(`/api/v1/talents/${talentId}/cover-letters/${id}`)
      await load()
    },
    [talentId, load],
  )

  return { coverLetters, loading, error, reload: load, generate, generating, update, remove }
}
